import mapKeys from 'lodash/fp/mapKeys';
import get from 'lodash/get';

import { breakpoint } from './index';

const getCSSVarName = (name: string) => name.replace(/var\((.*)\)/, '$1');

export const createVar = (name: string) => `var(--${name})`;
export const fallbackVar = (CSSVar: string, fallback: string) =>
  `var(${getCSSVarName(CSSVar)}, ${fallback})`;

type CSSVars = Record<string, string>;
export const vars = (CSSVars: CSSVars) => mapKeys(getCSSVarName, CSSVars);

type CSSMeasures = 'rem' | 'em' | 'px' | 'vh' | 'vw' | 'ch' | 'fr' | '%';
type CSSLength = `${number}${CSSMeasures}`;
export type Breakpoint = keyof typeof breakpoint.screen;

const getBreakPoint = (bp: Breakpoint | CSSLength) =>
  get(breakpoint.screen, bp, bp);

type BreakpointPair = [Breakpoint | CSSLength, CSSLength | string];

const getMediaScreen =
  (reducer: (z: string, bpp: BreakpointPair) => string) =>
  (init: CSSLength | string, ...args: Array<BreakpointPair>): string =>
    args.reduce(reducer, init);

/**
 * Applies a size for a the given breakpoint based on the folowing rule:
 *  > Every **NEXT** argument must be **GREATER** than the previous one.
 *
 * Alternatively you could use `sizeDown` for the opposite direction.
 *
 * Notes:
 * * The first argument is the default value (or minimum size: mobile)
 * * The resulting string is a calculation not a `@media` query
 *
 * @example
 *
 * Good: `10rem` < `20rem`, ex:
 * ```
 * width: sizeUp('10rem', ['md', '20rem']),
 * ```
 *
 * ---
 * Wrong: `20rem` < `10rem`, ex:
 * ```
 * width: sizeUp('20rem', ['md', '10rem']),
 * ```
 * @see `sizeDown` for ↑ case
 */
export const sizeUp = getMediaScreen(
  (z, [bp, val]) =>
    `clamp(${z}, (100vw - ${getBreakPoint(bp)} + 1rem) * 1000, ${val})`
);

/**
 * Applies a size for a the given breakpoint based on the folowing rule:
 * > Every **NEXT** argument must be **LESSER** than the previous one.
 *
 * Alternatively you could use `sizeUp` for the opposite direction.
 *
 * Notes:
 * * The first argument is the default value (or minimum size: mobile)
 * * The resulting string is a calculation not a `@media` query
 *
 * @example
 *
 * Good: `20rem` < `10rem`, ex:
 * ```
 * width: sizeDown('20rem', ['md', '10rem']),
 * ```
 *
 * ---
 *
 * Wrong: `10rem` < `20rem`, ex:
 * ```
 * width: sizeDown('10rem', ['md', '20rem']),
 * ```
 * @see `sizeUp` for ↑ case
 */
export const sizeDown = getMediaScreen(
  (z, [bp, val]) => `clamp(${val}, (${getBreakPoint(bp)} - 100vw) * 1000, ${z})`
);
